import styled from '@emotion/styled';
import { Box, InputLabel, Paper, TextField, Typography } from '@mui/material';

/** Matches the FormInput look: soft floating card inputs on the signup form. */
const INPUT_SHADOW = '0 6px 40px rgba(208, 208, 208, 0.4)';
const LABEL_COLOR = '#171717';
const TEXT_COLOR = '#333';
const PLACEHOLDER_COLOR = '#9CA3AF';
const MUTED_COLOR = '#6b7280';

export const FieldContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

export const NewSchoolFieldContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
`;

export const FieldLabel = styled(InputLabel)`
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 16px;
  color: ${LABEL_COLOR};
  white-space: normal;
`;

/**
 * Standard-variant text field skinned like FormInput (70px pill card, no
 * underline). Used for both the autocomplete input and the new-school input.
 */
export const SchoolTextField = styled(TextField)`
  & .MuiInputBase-root {
    min-height: 70px;
    border-radius: 16px;
    background: #fff;
    box-shadow: ${INPUT_SHADOW};
    padding: 3px 48px 3px 16px;
    font-size: 16px;
    color: ${TEXT_COLOR};

    &::before,
    &::after {
      content: none;
    }
  }

  & .MuiInputBase-input {
    font-size: 16px;
    color: ${TEXT_COLOR};

    &::placeholder {
      color: ${PLACEHOLDER_COLOR};
      opacity: 1;
    }
  }

  & .MuiAutocomplete-endAdornment {
    right: 16px;

    & .MuiSvgIcon-root {
      color: ${MUTED_COLOR};
      transition: transform 0.2s ease;
    }
  }
`;

/** Dropdown panel: rounded card with the same soft elevation as the inputs. */
export const SchoolDropdownPaper = styled(Paper)`
  margin-top: 8px;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(150, 150, 150, 0.28);
  overflow: hidden;

  & .MuiAutocomplete-listbox {
    padding: 6px;
    max-height: 42vh;
  }

  & .MuiAutocomplete-option {
    border-radius: 10px;
    min-height: 48px;
    margin: 2px 0;
    padding: 10px 12px;
    font-size: 15px;
    color: ${TEXT_COLOR};

    &.Mui-focused,
    &[aria-selected='true'] {
      background: rgba(0, 0, 0, 0.05);
    }
  }

  & .MuiAutocomplete-noOptions {
    font-size: 14px;
    color: ${MUTED_COLOR};
  }
`;

export const OptionRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  & .MuiSvgIcon-root {
    font-size: 20px;
    color: ${MUTED_COLOR};
  }
`;

/** "Other / Add new school" row: separated and emphasised as an action. */
export const AddNewOptionRow = styled(OptionRow)`
  border-top: 1px dashed rgba(0, 0, 0, 0.14);
  margin-top: 4px;
  padding-top: 10px;
  font-weight: 600;

  & .MuiSvgIcon-root {
    color: ${LABEL_COLOR};
  }
`;

export const OptionText = styled(Typography)`
  font-size: 15px;
  color: inherit;
  line-height: 1.35;
`;

export const FieldErrorText = styled(Typography)`
  font-size: 15px;
  color: red;
  margin-top: 8px;
`;

export const FieldHintRow = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;

  & .MuiSvgIcon-root {
    font-size: 16px;
    margin-top: 1px;
    color: ${MUTED_COLOR};
  }
`;

export const FieldHintText = styled(Typography)`
  font-size: 13px;
  line-height: 1.5;
  color: ${MUTED_COLOR};
`;
